import React, { useRef, useState } from 'react'
import { WidgetProps } from '@rjsf/utils'
import styled, { css } from 'styled-components'

import { FeedbotAce } from '../../../FeedbotAce/FeedbotAce'

type PromptMode = 'default' | 'inject' | 'custom'

type DefaultPromptEditorUiSchema = WidgetProps['uiSchema'] & {
  focus?: boolean
  resizeCssRule?: 'none' | 'both' | 'horizontal' | 'vertical' | 'block' | 'inline'
  initialRowsCount?: number
  defaultPrompt?: string
}

const MODE_NOTES: Record<PromptMode, string> = {
  default:
    '🧩 Using the built-in system prompt while keeping it up-to-date. If you customize it, it is recommended to keep variables and output formatting.',
  custom:
    '⚠️ Using a custom prompt so auto updates are disabled. Prefix the prompt with + to just inject text into the system prompt instead, or clear the prompt to revert to default.',
  inject:
    '💅 Injecting this prompt into the built-in system prompt while keeping it up-to-date. Clear the prompt to revert to default.'
}

const defaultPromptChromeCss = css`
  background-color: #f7f7f7;
  border-color: #d9d9d9;
`

const defaultPromptTextCss = css`
  .ace_content,
  .ace_content *,
  .ace_cursor {
    color: #8c8c8c !important;
  }
`

export const DefaultPromptEditorWidget: React.FC<WidgetProps> = ({
  value,
  uiSchema,
  onChange,
  ...restProps
}) => {
  const { focus, resizeCssRule, initialRowsCount, defaultPrompt } = (uiSchema ??
    {}) as DefaultPromptEditorUiSchema

  const [isFocused, setIsFocused] = useState(false)
  const promptEditedRef = useRef(false)
  const promptValue = typeof value === 'string' ? value : ''
  const mode = getPromptMode(promptValue, defaultPrompt)
  const displayedValue = mode === 'default' ? defaultPrompt ?? '' : promptValue

  const handleFocus = () => {
    setIsFocused(true)
    promptEditedRef.current = false
  }

  const handleBlur = () => {
    setIsFocused(false)
    if (!promptEditedRef.current) {
      onChange('')
    }
  }

  const handleChange = (newValue: string) => {
    const normalizedValue = newValue.trim()
    const hasChanged = normalizedValue !== defaultPrompt
    promptEditedRef.current = hasChanged
    hasChanged && onChange(normalizedValue)
  }

  return (
    <Wrapper>
      <FeedbotAce
        {...restProps}
        isWrapTextEnabled
        focus={focus}
        resizeCssRule={resizeCssRule}
        value={displayedValue}
        onChange={handleChange}
        initialRowsCount={initialRowsCount}
        wrapperCss={getWrapperCss(mode, isFocused)}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      <Note>{MODE_NOTES[mode]}</Note>
    </Wrapper>
  )
}

export const getPromptMode = (value: string, defaultPrompt?: string): PromptMode => {
  if (defaultPrompt && !value.trim()) {
    return 'default'
  }

  return value.startsWith('+') ? 'inject' : 'custom'
}

const getWrapperCss = (mode: PromptMode, isFocused: boolean) =>
  mode === 'default'
    ? css`
        ${!isFocused ? defaultPromptChromeCss : ''}
        ${defaultPromptTextCss}
      `
    : undefined

const Wrapper = styled.div.attrs({ className: 'form-control' })`
  height: auto;
  min-height: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
`

const Note = styled.div`
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: #747474;
  text-align: left;
  font-style: italic;
  font-family: sans-serif;
`
